import type { FunctionComponent } from 'react';
import { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from 'react-oidc-context';

import { config } from '../../../../config/config';
import { ContentMarkdown } from '../../../shared/components/content-markdown/content-markdown';
import { TitledSection } from '../../../shared/components/titled-section/titled-section';
import { useToast } from '../../../shared/hooks/use-toast';
import { createStyleHelper } from '../../../shared/utils/class-names';
import { getErrorMessage } from '../../../shared/utils/convert-error';
import { exists } from '../../../shared/utils/filters';
import { SessionLimitContinuousForm } from '../../components/session-limit-continuous-form/session-limit-continuous-form';
import { SessionLimitContinuousHistoryTable } from '../../components/session-limit-continuous-history-table/session-limit-continuous-history-table';
import {
    ICreatorName,
    useDeleteUserSessionContinuousLimitMutation,
    useGetUserSessionContinuousLimitsQuery,
    useSessionLimitsContinuousUpdatedSubscription,
} from '../../queries/session-limit-continuous.graphql';

import styles from './session-limits-continuous.module.scss';

const style = createStyleHelper(styles, 'session-limits-continuous');

export const SessionLimitsContinuous: FunctionComponent = () => {
    const intl = useIntl();
    const auth = useAuth();
    const toast = useToast();
    const userProfileId = auth.user?.profile?.user_profile_id as string;

    const [{ data: userSessionLimitsData }, refetch] = useGetUserSessionContinuousLimitsQuery({
        pause: !userProfileId,
        variables: {
            xTenantID: config.TENANT_ID,
            userProfileId: userProfileId as string,
        },
        requestPolicy: 'cache-and-network',
    });

    const [_, execute] = useDeleteUserSessionContinuousLimitMutation();
    const [{ data }] = useSessionLimitsContinuousUpdatedSubscription({
        pause: !userProfileId,
        variables: {
            userProfileId: userProfileId as string,
        },
    });

    useEffect(() => {
        if (data?.sessionLimitsContinuousUpdated) {
            refetch();
        }
    }, [data, refetch]);

    const onLimitCancel = useCallback(
        async (limitId: string) => {
            const { error } = await execute({
                userProfileId: userProfileId,
                xTenantID: config.TENANT_ID,
                creatorName: ICreatorName.User,
                limitId: limitId,
            });

            if (error) {
                toast.error(getErrorMessage(intl, 'user.limits.cancelled', error));
                return;
            }

            toast.success(
                intl.formatMessage({
                    id: 'user.limit.cancelled.success',
                    defaultMessage: 'User Limit Cancelled',
                }),
            );
            refetch();
        },
        [execute, userProfileId, toast, intl, refetch],
    );

    const userLimits = userSessionLimitsData?.usersSessionLimitsContinuous;

    return (
        <div className={style()}>
            <SessionLimitContinuousForm />

            <TitledSection
                scrollable
                title={
                    <FormattedMessage
                        id="session-limit.history.title"
                        defaultMessage="Session Limits History"
                    />
                }
            >
                <SessionLimitContinuousHistoryTable
                    history={userLimits?.filter(exists) ?? []}
                    onLimitCancel={onLimitCancel}
                />
            </TitledSection>

            <section className={style('terms')}>
                <ContentMarkdown
                    markdown={intl.formatMessage({
                        id: 'session-limits.terms-and-conditions',
                    })}
                />
            </section>
        </div>
    );
};
